import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  deleteStudentRecord,
  ensureHouseholdId,
  loadStudents,
  onRemoteSession,
  remoteClient,
  saveStudent,
  subscribeChanges,
  updateExistingStudent,
} from "../data/remote";
import {
  ageForGrade,
  birthdayFromId,
  students as seedStudents,
  type School,
  type Student,
} from "../domain/data";
import type { StudentInput } from "../domain/importStudents";
import { mergeRosterSeeds, removeFromRoster } from "../domain/roster";

const STORAGE_KEY = "lift-app-students-v2";

export type { StudentInput };

type Stored = {
  students: Student[];
  removedIds: string[];
};

type StudentsContextValue = {
  students: Student[];
  getStudent: (id: string) => Student | undefined;
  findByEmail: (email: string) => Student | undefined;
  studentsByIds: (ids: string[]) => Student[];
  studentsBySchool: (school: School) => Student[];
  studentsByGuardian: (email: string) => Student[];
  upsertStudent: (input: StudentInput) => Promise<Student>;
  addStudent: (input: StudentInput) => Promise<Student>;
  updateStudent: (id: string, input: StudentInput) => Promise<Student>;
  removeStudent: (id: string) => Promise<void>;
  importStudents: (
    rows: Array<{ input: StudentInput; existingId?: string }>,
  ) => Promise<Student[]>;
};

const StudentsContext = createContext<StudentsContextValue | null>(null);

function cloneSeed(): Stored {
  return {
    students: seedStudents.map((student) => ({ ...student })),
    removedIds: [],
  };
}

function normalizeStudent(student: Student): Student {
  return {
    ...student,
    birthday:
      student.birthday ||
      seedStudents.find((item) => item.id === student.id)?.birthday ||
      birthdayFromId(student.id, student.grade),
  };
}

function readStore(): Stored {
  if (typeof window === "undefined") return cloneSeed();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return cloneSeed();
    const parsed = JSON.parse(raw) as Stored;
    if (!parsed || !Array.isArray(parsed.students)) return cloneSeed();
    const removedIds = Array.isArray(parsed.removedIds)
      ? parsed.removedIds.filter((id): id is string => typeof id === "string")
      : [];
    const students = mergeSeedStudents(
      parsed.students.map(normalizeStudent),
      removedIds,
    );
    const store = { students, removedIds };
    if (students.length !== parsed.students.length) persist(store);
    return store;
  } catch {
    return cloneSeed();
  }
}

function mergeSeedStudents(current: Student[], removedIds: string[]): Student[] {
  return mergeRosterSeeds(current, seedStudents, removedIds);
}

function persist(store: Stored) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* ignore quota / private mode */
  }
}

function newStudentId(given: string, family: string): string {
  if (remoteClient()) return crypto.randomUUID();
  return slugifyName(given, family);
}

function slugifyName(given: string, family: string): string {
  const base =
    `${given}-${family}`
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "student";
  return `${base}-${Date.now().toString(36)}`;
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

function studentFromInput(
  input: StudentInput,
  id: string,
  acceptance?: Student["acceptance"],
): Student {
  return {
    id,
    given: input.given.trim(),
    family: input.family.trim(),
    email: normalizeEmail(input.email),
    school: input.school,
    grade: input.grade,
    age: ageForGrade(input.grade),
    birthday: input.birthday,
    guardianName: input.guardianName.trim(),
    guardianEmail: normalizeEmail(input.guardianEmail),
    guardianPhone: input.guardianPhone.trim(),
    emergencyName: input.emergencyName.trim(),
    emergencyPhone: input.emergencyPhone.trim(),
    ...(acceptance ? { acceptance } : {}),
  };
}

export function StudentsProvider({ children }: { children: ReactNode }) {
  const [store, setStore] = useState<Stored>(() =>
    remoteClient() ? { students: [], removedIds: [] } : readStore(),
  );
  const storeRef = useRef(store);
  storeRef.current = store;
  const loadGen = useRef(0);

  const write = useCallback((next: Stored) => {
    const stored: Stored = {
      students: next.students,
      removedIds: next.removedIds ?? storeRef.current.removedIds,
    };
    storeRef.current = stored;
    setStore(stored);
    if (!remoteClient()) persist(stored);
  }, []);

  const reload = useCallback(async () => {
    const client = remoteClient();
    if (!client) return;
    const token = ++loadGen.current;
    try {
      const students = await loadStudents(client);
      if (token !== loadGen.current) return;
      const next = { students, removedIds: storeRef.current.removedIds };
      storeRef.current = next;
      setStore(next);
    } catch {
      /* keep the last snapshot */
    }
  }, []);

  useEffect(() => {
    const client = remoteClient();
    if (!client) return;
    const stopSession = onRemoteSession(client, () => {
      void reload();
    });
    const stop = subscribeChanges(client, ["students"], () => {
      void reload();
    });
    const onFocus = () => {
      if (document.visibilityState === "visible") void reload();
    };
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      stopSession();
      stop();
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [reload]);

  const upsertStudent = useCallback(
    async (input: StudentInput) => {
      const current = storeRef.current;
      const email = normalizeEmail(input.email);
      const existing = email
        ? current.students.find(
            (student) => student.email.toLowerCase() === email,
          )
        : current.students.find(
            (student) =>
              student.given.toLowerCase() === input.given.trim().toLowerCase() &&
              student.family.toLowerCase() === input.family.trim().toLowerCase() &&
              student.birthday === input.birthday,
          );
      const student = studentFromInput(
        input,
        existing?.id ?? newStudentId(input.given, input.family),
        existing?.acceptance,
      );

      const client = remoteClient();
      if (client) {
        const { data: authData } = await client.auth.getSession();
        if (!authData.session) {
          throw new Error("Sign in required to save this registration.");
        }
      }
      write({
        students: existing
          ? current.students.map((item) =>
              item.id === existing.id ? student : item,
            )
          : [student, ...current.students],
        removedIds: current.removedIds,
      });
      if (client) {
        try {
          const householdId = await ensureHouseholdId(client);
          await saveStudent(client, student, householdId);
          await reload();
        } catch (error) {
          await reload();
          throw error instanceof Error ? error : new Error("Could not save that student.");
        }
      }
      return student;
    },
    [reload, write],
  );

  const importStudents = useCallback(
    async (rows: Array<{ input: StudentInput; existingId?: string }>) => {
      let students = storeRef.current.students;
      const saved: Student[] = [];
      rows.forEach((row) => {
        const existing = row.existingId
          ? students.find((item) => item.id === row.existingId)
          : undefined;
        const student = studentFromInput(
          row.input,
          existing?.id ?? newStudentId(row.input.given, row.input.family),
          existing?.acceptance,
        );
        students = existing
          ? students.map((item) => (item.id === existing.id ? student : item))
          : [student, ...students];
        saved.push(student);
      });
      write({ students, removedIds: storeRef.current.removedIds });
      const client = remoteClient();
      if (client) {
        try {
          for (const student of saved) {
            await saveStudent(client, student, null);
          }
          await reload();
        } catch {
          await reload();
          throw new Error("Could not save the roster.");
        }
      }
      return saved;
    },
    [reload, write],
  );

  const addStudent = useCallback(
    async (input: StudentInput) => {
      const current = storeRef.current;
      const student = studentFromInput(input, newStudentId(input.given, input.family));
      write({
        students: [student, ...current.students],
        removedIds: current.removedIds,
      });
      const client = remoteClient();
      if (client) {
        try {
          const { data: authData } = await client.auth.getSession();
          if (!authData.session) {
            throw new Error("Sign in required to add a student.");
          }
          await saveStudent(client, student, null);
          await reload();
        } catch (error) {
          await reload();
          throw error instanceof Error ? error : new Error("Could not add that student.");
        }
      }
      return student;
    },
    [reload, write],
  );

  const updateStudent = useCallback(
    async (id: string, input: StudentInput) => {
      const current = storeRef.current;
      const existing = current.students.find((student) => student.id === id);
      if (!existing) throw new Error("That student is no longer on the roster.");
      const student = studentFromInput(input, id, existing.acceptance);
      write({
        students: current.students.map((item) => (item.id === id ? student : item)),
        removedIds: current.removedIds,
      });
      const client = remoteClient();
      if (client) {
        try {
          await updateExistingStudent(client, student);
          await reload();
        } catch (error) {
          await reload();
          throw error instanceof Error ? error : new Error("Could not update that student.");
        }
      }
      return student;
    },
    [reload, write],
  );

  const removeStudent = useCallback(
    async (id: string) => {
      const current = storeRef.current;
      write(removeFromRoster(current.students, current.removedIds, id));
      const client = remoteClient();
      if (!client) return;
      try {
        await deleteStudentRecord(client, id);
        await reload();
      } catch (error) {
        await reload();
        throw error instanceof Error ? error : new Error("Could not remove that student.");
      }
    },
    [reload, write],
  );

  const value = useMemo<StudentsContextValue>(
    () => ({
      students: store.students,
      getStudent: (id) => store.students.find((student) => student.id === id),
      findByEmail: (email) =>
        store.students.find(
          (student) => student.email.toLowerCase() === normalizeEmail(email),
        ),
      studentsByIds: (ids) =>
        ids
          .map((id) => store.students.find((student) => student.id === id))
          .filter((student): student is Student => Boolean(student)),
      studentsBySchool: (school) =>
        store.students.filter((student) => student.school === school),
      studentsByGuardian: (email) => {
        const key = normalizeEmail(email);
        return store.students.filter(
          (student) => student.guardianEmail.toLowerCase() === key,
        );
      },
      upsertStudent,
      addStudent,
      updateStudent,
      removeStudent,
      importStudents,
    }),
    [store, upsertStudent, addStudent, updateStudent, removeStudent, importStudents],
  );

  return <StudentsContext.Provider value={value}>{children}</StudentsContext.Provider>;
}

export function useStudents(): StudentsContextValue {
  const ctx = useContext(StudentsContext);
  if (!ctx) throw new Error("useStudents must be used within StudentsProvider");
  return ctx;
}
